import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, CheckCircle2, CalendarRange, Loader2, Clock } from "lucide-react";
import { useDateFormat } from "@/lib/dateFormat";

function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - day);
  return x;
}
function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayIso() { return iso(new Date()); }

export default function RosterPage() {
  const { locale, formatDate } = useDateFormat();
  const [weekStart, setWeekStart] = useState(() => iso(mondayOf(new Date())));
  const [dept, setDept] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depts, setDepts] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({ start: "09:00", end: "17:00", role: "" });
  const [busy, setBusy] = useState(false);

  const wdFmt = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: "short" }), [locale]);
  const dnFmt = useMemo(() => new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" }), [locale]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ week_start: weekStart });
      if (dept) q.set("department", dept);
      const { data } = await api.get(`/workforce/roster?${q}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [weekStart, dept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/workforce/staff").then((r) => setDepts(r.data.departments || [])).catch(() => {});
  }, []);

  const days = useMemo(() => {
    const base = new Date(weekStart);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base); d.setDate(d.getDate() + i);
      const isoDay = iso(d);
      return {
        iso: isoDay,
        weekday: wdFmt.format(d),
        label: dnFmt.format(d),
        isToday: isoDay === todayIso(),
        isWeekend: [0, 6].includes(d.getDay()),
      };
    });
  }, [weekStart, wdFmt, dnFmt]);

  const weekEndIso = days[6]?.iso;

  const shiftFor = (empId, day) =>
    (data?.shifts || []).find((s) => s.employee_id === empId && s.date === day);

  const moveWeek = (delta) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + delta * 7); setWeekStart(iso(d));
  };

  const openAdd = (emp, day) => {
    setForm({ start: "09:00", end: "17:00", role: emp.job_title || "" });
    setDlg({ mode: "add", employee_id: emp.id, employee_name: emp.name, date: day });
  };
  const openEdit = (sh) => {
    setForm({ start: sh.start, end: sh.end, role: sh.role || "" });
    setDlg({ mode: "edit", ...sh });
  };

  const saveShift = async () => {
    setBusy(true);
    try {
      if (dlg.mode === "add") {
        await api.post("/workforce/roster", { employee_id: dlg.employee_id, date: dlg.date, ...form });
      } else {
        await api.patch(`/workforce/roster/${dlg.id}`, form);
      }
      toast.success("Shift saved");
      setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const deleteShift = async () => {
    setBusy(true);
    try {
      await api.delete(`/workforce/roster/${dlg.id}`);
      toast.success("Shift removed");
      setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const publish = async () => {
    try {
      const r = await api.post("/workforce/roster/publish", { week_start: weekStart, department: dept || undefined });
      toast.success(`Published ${r.data.published} shifts`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalShifts = (data?.shifts || []).length;

  return (
    <div className="space-y-5">
      <PageHeader title="Roster" subtitle="Plan weekly shifts for your team, then publish." />

      {/* Controls */}
      <Card className="p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 rounded-xl border border-hocp-border bg-hocp-alice/40 p-1">
          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white" onClick={() => moveWeek(-1)} data-testid="roster-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-2 min-w-[190px] text-center">
            <div className="flex items-center justify-center gap-1.5 text-[14px] font-semibold text-hocp-textPrimary">
              <CalendarRange className="w-4 h-4 text-hocp-mintDeep" />
              {formatDate(weekStart)} – {weekEndIso ? formatDate(weekEndIso) : ""}
            </div>
            <div className="text-[11px] text-hocp-textTertiary mt-0.5">Week view · {totalShifts} shift{totalShifts === 1 ? "" : "s"}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white" onClick={() => moveWeek(1)} data-testid="roster-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <Button variant="outline" size="sm" onClick={() => setWeekStart(iso(mondayOf(new Date())))} data-testid="roster-today">
          This week
        </Button>

        <select value={dept} onChange={(e) => setDept(e.target.value)}
                className="h-9 rounded-lg border border-hocp-border bg-white px-3 text-sm text-hocp-textPrimary focus:outline-none focus:ring-2 focus:ring-hocp-mint/40" data-testid="roster-dept">
          <option value="">All departments</option>
          {depts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>

        <div className="flex-1" />

        {data?.published
          ? <Badge tone="success"><CheckCircle2 className="w-3 h-3 mr-1 inline" />Published</Badge>
          : <Badge tone="warning">Draft</Badge>}
        <Button onClick={publish} data-testid="roster-publish"><CheckCircle2 className="w-4 h-4 mr-1.5" />Publish week</Button>
      </Card>

      {/* Grid */}
      {loading ? (
        <Card className="p-16 text-center text-hocp-textSecondary"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
              <thead>
                <tr className="bg-hocp-alice/60">
                  <th className="sticky left-0 z-10 bg-hocp-alice/95 backdrop-blur px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-hocp-textTertiary border-b border-hocp-border w-[220px]">
                    Employee
                  </th>
                  {days.map((d) => (
                    <th key={d.iso}
                        className={`px-2 py-2.5 text-center border-b border-l border-hocp-border ${d.isToday ? "bg-hocp-mintSoft" : d.isWeekend ? "bg-hocp-alice/80" : ""}`}>
                      <div className={`text-[12px] font-bold uppercase tracking-wide ${d.isToday ? "text-hocp-mintDeep" : "text-hocp-textSecondary"}`}>{d.weekday}</div>
                      <div className={`text-[13px] font-semibold mt-0.5 ${d.isToday ? "text-hocp-mintDeep" : "text-hocp-textPrimary"}`}>{d.label}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.staff || []).map((emp, ri) => (
                  <tr key={emp.id} className={`border-b border-hocp-border ${ri % 2 ? "bg-hocp-alice/20" : "bg-white"}`} data-testid={`roster-row-${emp.id}`}>
                    <td className={`sticky left-0 z-10 px-4 py-3 border-r border-hocp-border ${ri % 2 ? "bg-hocp-alice/95" : "bg-white"}`}>
                      <div className="font-semibold text-[14px] text-hocp-textPrimary leading-tight">{emp.name}</div>
                      <div className="text-[12px] text-hocp-textTertiary mt-0.5">{emp.department}{emp.job_title ? ` · ${emp.job_title}` : ""}</div>
                    </td>
                    {days.map((d) => {
                      const sh = shiftFor(emp.id, d.iso);
                      return (
                        <td key={d.iso} className={`px-2 py-2 border-l border-hocp-border align-middle ${d.isToday ? "bg-hocp-mintSoft/40" : d.isWeekend ? "bg-hocp-alice/20" : ""}`}>
                          {sh ? (
                            <button onClick={() => openEdit(sh)}
                              className="w-full rounded-lg bg-hocp-mintSoft border border-hocp-mintDeep/40 px-2 py-2 text-center transition-colors hover:bg-hocp-mint/30 hover:border-hocp-mintDeep"
                              data-testid={`shift-${emp.id}-${d.iso}`}>
                              <div className="flex items-center justify-center gap-1 text-[13px] font-bold text-hocp-deepTeal tabular-nums">
                                <Clock className="w-3 h-3" />{sh.start}–{sh.end}
                              </div>
                              {sh.role && <div className="text-[10px] text-hocp-deepTeal/70 truncate mt-0.5">{sh.role}</div>}
                            </button>
                          ) : (
                            <button onClick={() => openAdd(emp, d.iso)}
                              className="w-full h-11 rounded-lg border border-dashed border-hocp-border text-hocp-textTertiary flex items-center justify-center transition-colors hover:border-hocp-mintDeep hover:text-hocp-mintDeep hover:bg-hocp-mintSoft/40"
                              data-testid={`add-${emp.id}-${d.iso}`}>
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {(data?.staff || []).length === 0 && (
                  <tr><td colSpan={8} className="text-center py-16 text-hocp-textSecondary">No staff for this department</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{dlg?.mode === "add" ? "Add shift" : "Edit shift"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Set the start time, end time and role for this shift.</DialogDescription>
          <div className="space-y-3">
            <div className="text-sm text-hocp-textSecondary">
              <span className="font-medium text-hocp-textPrimary">{dlg?.employee_name}</span> · {dlg?.date ? formatDate(dlg.date) : ""}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start</Label><Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="shift-start" /></div>
              <div><Label>End</Label><Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="shift-end" /></div>
            </div>
            <div><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Front Desk Agent" /></div>
          </div>
          <DialogFooter className="gap-2">
            {dlg?.mode === "edit" && (
              <Button variant="outline" onClick={deleteShift} disabled={busy} data-testid="shift-delete"><Trash2 className="w-4 h-4 mr-1" />Remove</Button>
            )}
            <Button onClick={saveShift} disabled={busy} data-testid="shift-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
